import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useMutation } from 'react-query';
import MAIN_INTRODUCTION from './images/main_introduction.png';
import RADIO_CHECKED from './images/radio-checked.png';
import RADIO_UNCHECKED from './images/radio-unchecked.png';
import SUCCESS_CHECK from './images/success_check.png';
import KTP_LOGO from './images/ktp_logo.png';
import { postJandiWebhook } from './api/jandi';
import {
  MedicalTourismApplyFormData,
  MedicalTourismApplyFormErrors,
} from './types';
import { createJandiMessage } from './util/jandiMessageTemplate';

const TARGET_ROOM_ID = 'fc0590f21bd96544b22b3d43f7e75a7b';

export default function MedicalTourismApply() {
  const [form, setForm] = useState<MedicalTourismApplyFormData>({
    selectedService: 'all',
    hospitalName: '',
    applicantName: '',
    email: '',
    phone: '',
    position: '',
  });

  const [errors, setErrors] = useState<MedicalTourismApplyFormErrors>({});

  const [isCompleted, setIsCompleted] = useState(false);

  // 이 페이지에서만 body의 min-width를 0으로 설정
  useEffect(() => {
    // 기존 min-width 값을 저장
    const originalMinWidth = document.body.style.minWidth;

    // min-width를 0으로 설정
    document.body.style.minWidth = '0';

    // 컴포넌트 언마운트 시 원래 값으로 복원
    return () => {
      document.body.style.minWidth = originalMinWidth;
    };
  }, []);

  // Jandi 웹훅 mutation
  const jandiMutation = useMutation(
    (formData: MedicalTourismApplyFormData) => {
      const payload = createJandiMessage(formData);
      // target_room_id는 필요에 따라 변경하세요
      return postJandiWebhook(payload, TARGET_ROOM_ID);
    },
    {
      onSuccess: () => {
        console.log('Jandi 알림 전송 성공');
      },
      onError: (error) => {
        console.error('Jandi 알림 전송 실패:', error);
      },
    },
  );

  // 이메일 validation
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // 휴대폰 번호 validation (숫자만)
  const validatePhone = (phone: string): boolean => {
    const phoneRegex = /^[0-9]*$/;
    return phoneRegex.test(phone);
  };

  // 입력값 변경 핸들러
  const handleInputChange = (
    field: keyof MedicalTourismApplyFormData,
    value: string,
  ) => {
    if (field === 'phone') {
      // 휴대폰 번호는 숫자만 입력 가능
      if (!validatePhone(value)) {
        return;
      }
    }

    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    // 실시간 validation
    if (field === 'email' && value) {
      if (!validateEmail(value)) {
        setErrors((prev) => ({
          ...prev,
          email: '올바른 이메일 형식이 아닙니다',
        }));
      } else {
        setErrors((prev) => {
          const { email, ...rest } = prev;
          return rest;
        });
      }
    }
  };

  // 서비스 선택 핸들러
  const handleServiceSelect = (service: 'all' | 'payment' | 'reservation') => {
    setForm((prev) => ({
      ...prev,
      selectedService: service,
    }));
  };

  // 폼 제출 핸들러
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // validation 체크
    const newErrors: MedicalTourismApplyFormErrors = {};

    // 필수 필드 체크
    if (!form.hospitalName.trim()) {
      alert('병원명을 입력해주세요.');
      return;
    }
    if (!form.applicantName.trim()) {
      alert('성함을 입력해주세요.');
      return;
    }
    if (!form.email.trim()) {
      alert('이메일을 입력해주세요.');
      return;
    }
    if (!form.phone.trim()) {
      alert('휴대폰번호를 입력해주세요.');
      return;
    }
    if (!form.position.trim()) {
      alert('직책을 입력해주세요.');
      return;
    }

    // 이메일 형식 체크
    if (!validateEmail(form.email)) {
      newErrors.email = '올바른 이메일 형식이 아닙니다';
    }

    // 휴대폰 번호 형식 체크
    if (form.phone && !validatePhone(form.phone)) {
      newErrors.phone = '숫자만 입력 가능합니다';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Jandi 웹훅 호출
    jandiMutation.mutate(form, {
      onSuccess: () => {
        setIsCompleted(true);
        // 폼 초기화
        setForm({
          selectedService: 'all',
          hospitalName: '',
          applicantName: '',
          email: '',
          phone: '',
          position: '',
        });
      },
      onError: () => {
        alert(
          '서비스 신청 중 오류가 발생했습니다.\n잠시 후 다시 시도해주세요.',
        );
      },
    });
  };

  // 완료 화면
  if (isCompleted) {
    return (
      <Wrapper>
        <CompletionContainer>
          <SuccessCheck src={SUCCESS_CHECK} alt='success check' />
          <CompletionTitle>서비스 신청이 완료되었습니다.</CompletionTitle>
          <CompletionDescription>
            입력하신 정보로
            <br />
            영업일 기준 하루내로 연락드리겠습니다.
          </CompletionDescription>
          <LogoContainer>
            <LogoImage src={KTP_LOGO} alt='KTP Logo' />
          </LogoContainer>
        </CompletionContainer>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <HeroImage src={MAIN_INTRODUCTION} alt='main introduction' />

      <FormContainer onSubmit={handleSubmit}>
        <FormContent>
          {/* 헤더 */}
          <HeaderSection>
            <MainTitle>
              가격 경쟁력 강화 & <br />
              외국인 환자 유치 서비스 신청
            </MainTitle>
            <Description>
              입력하시는 정보는 신청을 위한
              <br />
              정보 수집 목적으로만 사용됩니다.
            </Description>
          </HeaderSection>

          {/* 관심 서비스 선택 */}
          <Section>
            <SectionTitle>관심 서비스를 선택해주세요</SectionTitle>
            <ServiceButtonsWrapper>
              <ServiceButtonRow>
                <ServiceButton
                  type='button'
                  onClick={() => handleServiceSelect('all')}
                  $isSelected={form.selectedService === 'all'}
                >
                  <RadioCircle>
                    <RadioInner
                      src={
                        form.selectedService === 'all'
                          ? RADIO_CHECKED
                          : RADIO_UNCHECKED
                      }
                      alt='radio checked'
                    />
                  </RadioCircle>
                  <ServiceText
                    $isSelected={form.selectedService === 'all'}
                    $isBold
                  >
                    전체
                  </ServiceText>
                </ServiceButton>
                <ServiceButton
                  type='button'
                  onClick={() => handleServiceSelect('payment')}
                  $isSelected={form.selectedService === 'payment'}
                >
                  <RadioCircle>
                    <RadioInner
                      src={
                        form.selectedService === 'payment'
                          ? RADIO_CHECKED
                          : RADIO_UNCHECKED
                      }
                      alt='radio checked'
                    />
                  </RadioCircle>
                  <ServiceText $isSelected={form.selectedService === 'payment'}>
                    간편 결제 솔루션
                  </ServiceText>
                </ServiceButton>
              </ServiceButtonRow>
              <ServiceButton
                type='button'
                onClick={() => handleServiceSelect('reservation')}
                $isSelected={form.selectedService === 'reservation'}
              >
                <RadioCircle>
                  <RadioInner
                    src={
                      form.selectedService === 'reservation'
                        ? RADIO_CHECKED
                        : RADIO_UNCHECKED
                    }
                    alt='radio checked'
                  />
                </RadioCircle>
                <ServiceText
                  $isSelected={form.selectedService === 'reservation'}
                >
                  외국인 전용 예약 솔루션
                </ServiceText>
              </ServiceButton>
            </ServiceButtonsWrapper>
          </Section>

          {/* 병원 정보 */}
          <Section>
            <SectionTitle>병원 정보</SectionTitle>
            <InputGroup>
              <Label>
                병원명 <Required>*</Required>
              </Label>
              <Input
                type='text'
                placeholder='병원 이름을 입력하세요'
                value={form.hospitalName}
                onChange={(e) =>
                  handleInputChange('hospitalName', e.target.value)
                }
              />
            </InputGroup>
          </Section>

          {/* 신청인 정보 */}
          <Section>
            <SectionTitle>신청인 정보</SectionTitle>
            <InputGroupsContainer>
              {/* 성함 */}
              <InputGroup>
                <Label>
                  성함 <Required>*</Required>
                </Label>
                <Input
                  type='text'
                  placeholder='신청하시는 분의 성함을 적어주세요'
                  value={form.applicantName}
                  onChange={(e) =>
                    handleInputChange('applicantName', e.target.value)
                  }
                />
              </InputGroup>

              {/* 이메일 */}
              <InputGroup>
                <Label>
                  이메일 <Required>*</Required>
                </Label>
                <Input
                  type='email'
                  placeholder='연락받으실 이메일 주소를 적어주세요'
                  value={form.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  $hasError={!!errors.email}
                />
                {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
              </InputGroup>

              {/* 휴대폰번호 */}
              <InputGroup>
                <Label>
                  휴대폰번호 <Required>*</Required>
                </Label>
                <Input
                  type='tel'
                  placeholder='연락받으실 휴대폰 번호를 적어주세요'
                  value={form.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  $hasError={!!errors.phone}
                />
                {errors.phone && <ErrorMessage>{errors.phone}</ErrorMessage>}
              </InputGroup>

              {/* 직책 */}
              <InputGroup>
                <Label>
                  직책 <Required>*</Required>
                </Label>
                <Input
                  type='text'
                  placeholder='직책을 입력해주세요 (ex. 대표원장, 실장님, 직원 등)'
                  value={form.position}
                  onChange={(e) =>
                    handleInputChange('position', e.target.value)
                  }
                />
              </InputGroup>
            </InputGroupsContainer>
          </Section>

          {/* 제출 버튼 */}
          <SubmitButton type='submit' disabled={jandiMutation.isLoading}>
            {jandiMutation.isLoading ? '전송 중...' : '서비스 신청하기'}
          </SubmitButton>
        </FormContent>
      </FormContainer>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
`;

const HeroImage = styled.img`
  width: 100%;
  display: block;
`;

const FormContainer = styled.form`
  padding: 48px 20px;
  max-width: 500px;
  margin: 0 auto;
`;

const FormContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const MainTitle = styled.h2`
  color: #04368d;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.4px;
  line-height: 1.5;
  margin-bottom: 23px;
  font-family: Pretendard, sans-serif;
`;

const Description = styled.p`
  color: #1c1c1c;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.28px;
  line-height: 1.5;
  font-family: Pretendard, sans-serif;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const SectionTitle = styled.h3`
  color: #1c1c1c;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.36px;
  line-height: 1.5;
  font-family: Pretendard, sans-serif;
`;

const ServiceButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ServiceButtonRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const ServiceButton = styled.button<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 30px 18px 16px;
  border-radius: 120px;
  border: 2px solid ${(props) => (props.$isSelected ? '#0055e2' : '#dcdcdd')};
  background: ${(props) => (props.$isSelected ? '#0055e2' : '#fff')};
  color: ${(props) => (props.$isSelected ? '#fff' : '#171719')};
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${(props) =>
    props.$isSelected ? 'inset 0px 0px 4px 0px #1757c5' : 'none'};
  width: fit-content;

  &:hover {
    opacity: 0.9;
  }
`;

const RadioCircle = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const RadioInner = styled.img`
  width: 24px;
  height: 24px;
`;

const ServiceText = styled.span<{ $isSelected: boolean; $isBold?: boolean }>`
  font-size: 16px;
  font-weight: ${(props) => (props.$isBold ? 800 : 600)};
  display: flex;
  align-items: center;
`;

const InputGroupsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  color: #1f1e1c;
  font-size: 14px;
  font-weight: 600;
  font-family: Pretendard, sans-serif;
`;

const Required = styled.span`
  color: #ff2d55;
`;

const Input = styled.input<{ $hasError?: boolean }>`
  width: 100%;
  padding: 16px;
  border: 1px solid ${(props) => (props.$hasError ? '#ff2d55' : '#a7a7a7')};
  border-radius: 8px;
  font-size: 14px;
  font-family: Pretendard, sans-serif;
  min-width: 0;

  &::placeholder {
    color: #777;
  }

  &:focus {
    outline: none;
    border-color: ${(props) => (props.$hasError ? '#ff2d55' : '#0055e2')};
  }
`;

const ErrorMessage = styled.p`
  color: #ff2d55;
  font-size: 12px;
  font-family: Pretendard, sans-serif;
`;

const SubmitButton = styled.button`
  width: 100%;
  background: #246cf6;
  color: white;
  font-size: 18px;
  font-weight: 800;
  padding: 17px;
  border-radius: 12px;
  letter-spacing: -0.36px;
  cursor: pointer;
  transition: background 0.2s ease;
  font-family: Pretendard, sans-serif;

  &:hover:not(:disabled) {
    background: #1e5ad4;
  }

  &:disabled {
    background: #999;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const CompletionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
  text-align: center;
`;

const SuccessCheck = styled.img`
  width: 56px;
  height: 56px;
  margin-bottom: 22px;
`;

const CompletionTitle = styled.h2`
  color: #04368d;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.4px;
  line-height: 1.5;
  margin-bottom: 16px;
`;

const CompletionDescription = styled.p`
  color: #1c1c1c;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.28px;
  line-height: 1.5;
  margin-bottom: 173px;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 15px;
`;

const LogoImage = styled.img`
  height: 15px;
  width: auto;
  object-fit: contain;
`;
